import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Play, Users, Clock } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const GameStartCountdown = ({ isVisible, onCountdownEnd, gameName, playerCount }) => {
  const { t } = useTranslation('common')
  const [countdown, setCountdown] = useState(5)
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    if (!isVisible) return

    setCountdown(5)
    setIsAnimating(true)

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval)
          setTimeout(() => {
            onCountdownEnd()
          }, 0)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [isVisible, onCountdownEnd])

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        className="glass-effect rounded-xl p-8 text-center max-w-md w-full mx-4"
      >
        {/* Game Starting Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <div className="flex items-center justify-center gap-3 mb-2">
            <Play className="w-8 h-8 text-green-400" />
            <h2 className="text-2xl font-bold text-white">{t('gameStartCountdown.gameStarting')}</h2>
          </div>
          <p className="text-gray-300">{gameName}</p>
        </motion.div>

        {/* Countdown Circle */}
        <motion.div
          className="relative mb-6"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
        >
          <div className="w-32 h-32 mx-auto relative">
            {/* Background Circle */}
            <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="rgba(75, 85, 99, 0.3)"
                strokeWidth="8"
                fill="none"
              />
              {/* Progress Circle */}
              <motion.circle
                cx="50"
                cy="50"
                r="45"
                stroke="#10B981"
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 45}`}
                strokeDashoffset={`${2 * Math.PI * 45 * ((5 - countdown) / 5)}`}
                initial={{ strokeDashoffset: 2 * Math.PI * 45 }}
                animate={{ 
                  strokeDashoffset: 2 * Math.PI * 45 * ((5 - countdown) / 5),
                }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </svg>
            
            {/* Countdown Number */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.span
                key={countdown}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 1.5, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="text-4xl font-bold text-white"
              >
                {countdown > 0 ? countdown : '🎮'}
              </motion.span>
            </div>
          </div>
        </motion.div>

        {/* Player Count */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="flex items-center justify-center gap-2 text-gray-300"
        >
          <Users className="w-5 h-5" />
          <span>{t('gameStartCountdown.playersReady', { count: playerCount })}</span>
        </motion.div>

        {/* Status Messages */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-4"
        >
          {countdown > 0 ? (
            <p className="text-gray-400 text-sm">
              {t('gameStartCountdown.gameStartsIn', { count: countdown })}
            </p>
          ) : (
            <p className="text-green-400 text-sm font-medium">
              {t('gameStartCountdown.startingGameNow')}
            </p>
          )}
        </motion.div>

        {/* Pulse Animation for Final Second */}
        {countdown === 1 && (
          <motion.div
            className="absolute inset-0 border-4 border-green-400 rounded-xl"
            animate={{
              opacity: [0, 1, 0],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
            }}
          />
        )}
      </motion.div>
    </div>
  )
}

export default GameStartCountdown